import {
  getOfficeBillingHistory,
  sendBillingDocumentEmail,
  type BillingHistoryDoc,
  type BillingTemplateVars,
  type SendBillingDocumentPayload,
} from "../Services/Billing";
import type { AccountingOfficeDetail } from "./accountingOfficeDetail";
import { officeLegalName, toFeeContext } from "./accountingOfficeDetail";
import { getJurisdictionLabel } from "./jurisdiction/structuredJurisdiction";
import { resolveAccountingOfficeContacts } from "./accountingContactInheritance";
import {
  resolveBillingEmailAddressing,
  type BillingSendRecipients,
} from "./billingSendRecipients";
import {
  matchProfessionalsRange,
  parseProfessionalsCount,
} from "./accountingFeesCalculations";
import { fiscalYearFromIso, parseCalendarDate } from "./dates";
import {
  buildPaymentReceiptInputFromDoc,
  generateAccountingInvoicePDFBlob,
  generateAccountingPaymentReceiptPDFBlob,
} from "./generateAccountingInvoicePDF";

export type { BillingSendRecipients } from "./billingSendRecipients";

export interface ResolvedRecipient {
  email: string;
  name: string;
  /** Which contact the email resolved to. */
  source: "accounting" | "primary" | "none";
}

export interface SendAccountingResult {
  /** The address the email was actually delivered to. */
  sentTo: string;
  /** The intended primary contact recipient. */
  intended: ResolvedRecipient;
}

/** Resolves all delivery targets for a real billing send (with primary-office inheritance). */
export async function resolveBillingSendRecipients(
  office: AccountingOfficeDetail,
): Promise<BillingSendRecipients> {
  const resolution = await resolveAccountingOfficeContacts(office);
  return resolution.billingRecipients;
}

export function collectBillingDeliveryEmails(
  recipients: BillingSendRecipients,
): string[] {
  const addressing = resolveBillingEmailAddressing(recipients);
  return [addressing.toEmail, addressing.ccEmail].filter(Boolean);
}

export function formatBillingSendRecipientSummary(
  recipients: BillingSendRecipients,
): string {
  return collectBillingDeliveryEmails(recipients).join(", ") || "—";
}

function recipientsToIntended(
  recipients: BillingSendRecipients,
): ResolvedRecipient {
  const addressing = resolveBillingEmailAddressing(recipients);
  const source = recipients.accountingEmail.trim()
    ? "accounting"
    : recipients.primaryEmail.trim()
      ? "primary"
      : "none";
  return {
    email: addressing.toEmail,
    name: addressing.toName,
    source,
  };
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        resolve(result.split(",")[1] ?? "");
      } else {
        reject(new Error("Unexpected FileReader result."));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error("Read failed."));
    reader.readAsDataURL(blob);
  });
}

function sanitizeFilenamePart(value: string, max = 40): string {
  return (
    value
      .replace(/[^a-zA-Z0-9\s-]/g, "")
      .replace(/\s+/g, "_")
      .slice(0, max) || "Office"
  );
}

function resolveActualRecipient(
  recipients: BillingSendRecipients,
  isTest: boolean,
  testRecipients?: string[],
): { to: string | string[]; cc?: string } {
  if (isTest) {
    const emails = (testRecipients ?? [])
      .map((email) => email.trim())
      .filter(Boolean);
    if (emails.length === 0) {
      throw new Error("Select at least one staff recipient.");
    }
    return { to: emails };
  }

  const addressing = resolveBillingEmailAddressing(recipients);
  if (!addressing.toEmail) {
    throw new Error(
      "An accounting or primary contact email is required to send.",
    );
  }
  return {
    to: addressing.toEmail,
    cc: addressing.ccEmail || undefined,
  };
}

function formatSentToLabel(sentTo: string | string[], cc?: string): string {
  const toLabel = Array.isArray(sentTo) ? sentTo.join(", ") : sentTo;
  const ccLabel = cc?.trim();
  return ccLabel ? `${toLabel}, ${ccLabel}` : toLabel;
}

const DUES_PERIOD_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function formatDuesMonthYear(date: Date): string {
  return `${DUES_PERIOD_MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/**
 * Builds the "Membership Year" range for Template102 from the invoice fiscal year
 * (e.g. FY2027 / billing period 2026-2027). Month and year only — no day.
 * Example: "Aug 2026 - Jul 2027"
 */
function buildMembershipYear(
  invoice: BillingHistoryDoc,
  office: AccountingOfficeDetail,
): string {
  const fiscalYear =
    invoice.context?.fiscalYear ??
    (invoice.issueDate ? fiscalYearFromIso(invoice.issueDate) : undefined);
  if (!fiscalYear || Number.isNaN(fiscalYear)) {
    return "";
  }

  const anniversary = parseCalendarDate(
    office.billingStartDate as string | null | undefined,
  );
  const startMonth = anniversary ? anniversary.getUTCMonth() : 6;
  const startDay = anniversary ? anniversary.getUTCDate() : 1;

  const startDate = new Date(Date.UTC(fiscalYear - 1, startMonth, startDay));
  const endDate = new Date(Date.UTC(fiscalYear, startMonth, startDay));
  endDate.setUTCDate(endDate.getUTCDate() - 1);

  return `${formatDuesMonthYear(startDate)} - ${formatDuesMonthYear(endDate)}`;
}

/** Resolves the membership professionals-count label used in Template102. */
function resolveMembershipCategory(office: AccountingOfficeDetail): string {
  const count = parseProfessionalsCount(toFeeContext(office));
  if (count === null) {
    return "";
  }
  const range = matchProfessionalsRange(count);
  return range ? `${range} Professionals` : "";
}

function formatInvoiceTotal(total: unknown): string {
  const num = Number(total ?? 0);
  if (Number.isNaN(num)) {
    return "$0.00 USD";
  }
  const formatted = num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `$${formatted} USD`;
}

/**
 * Amount shown as Membership Dues. Matches the PDF total: invoice charges minus
 * the applied member balance (stored in totalPaid together with any payments).
 */
function membershipDuesAmountDue(invoice: BillingHistoryDoc): number {
  const total = Number(invoice.total) || 0;
  const totalPaid = Number(invoice.totalPaid) || 0;
  return Math.max(0, Math.round((total - totalPaid) * 100) / 100);
}

/** "2025-2026" style label used in the annual reminder. */
function buildAnnualFiscalYearLabel(invoice: BillingHistoryDoc): string {
  const fy =
    invoice.context?.fiscalYear ??
    (invoice.issueDate ? fiscalYearFromIso(invoice.issueDate) : undefined);
  if (!fy || Number.isNaN(Number(fy))) {
    return "";
  }
  return `${fy}-${Number(fy) + 1}`;
}

/** "2026" style label used in the assessment reminder. */
function buildAssessmentFiscalYearLabel(invoice: BillingHistoryDoc): string {
  const fy =
    invoice.context?.fiscalYear ??
    (invoice.issueDate ? fiscalYearFromIso(invoice.issueDate) : undefined);
  return fy ? String(fy) : "";
}

function annualTemplateVars(
  invoice: BillingHistoryDoc,
  office: AccountingOfficeDetail,
  contactName: string,
): BillingTemplateVars {
  return {
    fullname: contactName || officeLegalName(office),
    company: officeLegalName(office),
    jurisdiction: getJurisdictionLabel(office),
    category: resolveMembershipCategory(office),
    fromToMonthYear: buildMembershipYear(invoice, office),
    totalAmount: formatInvoiceTotal(membershipDuesAmountDue(invoice)),
    invoiceNum: String(invoice.invoiceNumber ?? invoice.id ?? "").trim(),
    firmId: String(office.legacyId ?? "").trim(),
  };
}

function annualReminderTemplateVars(
  invoice: BillingHistoryDoc,
  office: AccountingOfficeDetail,
  contactName: string,
): BillingTemplateVars {
  return {
    fullname: contactName || officeLegalName(office),
    company: officeLegalName(office),
    fiscalYear: buildAnnualFiscalYearLabel(invoice),
    ccList: "",
  };
}

function assessmentTemplateVars(
  invoice: BillingHistoryDoc,
  office: AccountingOfficeDetail,
  contactName: string,
): BillingTemplateVars {
  return {
    fullname: contactName || officeLegalName(office),
    company: officeLegalName(office),
    amount: formatInvoiceTotal(invoice.total),
  };
}

function assessmentReminderTemplateVars(
  invoice: BillingHistoryDoc,
  office: AccountingOfficeDetail,
  contactName: string,
): BillingTemplateVars {
  return {
    fullname: contactName || officeLegalName(office),
    company: officeLegalName(office),
    fiscalYear: buildAssessmentFiscalYearLabel(invoice),
  };
}

/**
 * Generates the accounting invoice PDF and emails it. When `test` is true it is
 * delivered only to the selected staff recipients.
 */
export async function sendAccountingInvoice(
  invoice: BillingHistoryDoc,
  office: AccountingOfficeDetail,
  options?: { test?: boolean; testRecipients?: string[] },
): Promise<SendAccountingResult> {
  const isTest = options?.test === true;
  const recipients = await resolveBillingSendRecipients(office);
  const intended = recipientsToIntended(recipients);
  const delivery = resolveActualRecipient(
    recipients,
    isTest,
    options?.testRecipients,
  );

  const blob = await generateAccountingInvoicePDFBlob({ invoice, office });
  const pdfBase64 = await blobToBase64(blob);

  const officeName = officeLegalName(office);
  const invoiceRef = invoice.invoiceNumber ?? invoice.id;
  const filename = `Invoice_${sanitizeFilenamePart(officeName)}_${sanitizeFilenamePart(
    invoiceRef,
    24,
  )}.pdf`;

  const kind = invoice.context?.kind ?? "";
  const contactName = intended.name || officeName;
  const isReminder = invoice.isSent === true;

  let emailPayload: SendBillingDocumentPayload;

  if (kind === "annual" || kind === "membershipDues") {
    if (isReminder) {
      emailPayload = {
        to: delivery.to,
        cc: delivery.cc,
        kind: "annual-reminder",
        subject: `WSG Membership Dues Reminder - ${officeName}`,
        templateVars: annualReminderTemplateVars(invoice, office, contactName),
        filename,
        pdfBase64,
      };
    } else {
      emailPayload = {
        to: delivery.to,
        cc: delivery.cc,
        kind: "annual",
        subject: `WSG Annual Dues - ${officeName}`,
        templateVars: annualTemplateVars(invoice, office, contactName),
        filename,
        pdfBase64,
      };
    }
  } else if (kind === "assessment" || kind === "special") {
    if (isReminder) {
      emailPayload = {
        to: delivery.to,
        cc: delivery.cc,
        kind: "assessment-reminder",
        subject: `WSG Assessment Reminder - ${officeName}`,
        templateVars: assessmentReminderTemplateVars(
          invoice,
          office,
          contactName,
        ),
        filename,
        pdfBase64,
      };
    } else {
      emailPayload = {
        to: delivery.to,
        cc: delivery.cc,
        kind: "assessment",
        subject: `WSG Assessment Invoice - ${officeName}`,
        templateVars: assessmentTemplateVars(invoice, office, contactName),
        filename,
        pdfBase64,
      };
    }
  } else {
    const subject =
      `Invoice ${invoice.invoiceNumber ?? ""}`.trim() + ` - ${officeName}`;
    const html =
      `<p>Dear ${contactName || officeName},</p>` +
      `<p>Please find attached your invoice${
        invoice.invoiceNumber ? ` ${invoice.invoiceNumber}` : ""
      }.</p>` +
      `<p>Kind regards,<br/>World Services Group</p>`;
    emailPayload = {
      to: delivery.to,
      cc: delivery.cc,
      subject,
      html,
      filename,
      pdfBase64,
    };
  }

  await sendBillingDocumentEmail(
    isTest ? { ...emailPayload, isTest: true } : emailPayload,
  );

  return {
    sentTo: formatSentToLabel(delivery.to, delivery.cc),
    intended,
  };
}

const PAYMENT_EPSILON = 0.005;

function formatPaymentEmailAmount(value: number): string {
  const decimals = Number.isInteger(value) ? 0 : 2;
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

interface PaymentConfirmationSummary {
  isPartial: boolean;
  /** Total paid so far on the invoices this payment is applied to. */
  paid: number;
  /** Total billed on those invoices. */
  invoiced: number;
}

/**
 * Mirrors classic payments.asp: the confirmation reports everything paid on
 * the invoice(s), not only this payment, and is "partial" while a balance remains.
 */
function summarizePaymentConfirmation(
  payment: BillingHistoryDoc,
  history: BillingHistoryDoc[],
): PaymentConfirmationSummary {
  const invoiceIds = new Set(
    (payment.allocations ?? [])
      .filter((a) => a.invoiceId && Number(a.amount) > 0)
      .map((a) => a.invoiceId),
  );
  const invoices = history.filter(
    (doc) => doc.type === "invoice" && invoiceIds.has(doc.id),
  );

  if (invoices.length === 0) {
    const amount = Number(payment.amount) || 0;
    return { isPartial: false, paid: amount, invoiced: amount };
  }

  const invoiced = invoices.reduce(
    (sum, inv) => sum + (Number(inv.total) || 0),
    0,
  );
  const paid = invoices.reduce(
    (sum, inv) => sum + (Number(inv.totalPaid) || 0),
    0,
  );
  return {
    isPartial: paid < invoiced - PAYMENT_EPSILON,
    paid,
    invoiced,
  };
}

/**
 * Generates the accounting payment receipt PDF and emails it with the full
 * (Template122) or partial (Template113) payment confirmation body.
 */
export async function sendAccountingPayment(
  payment: BillingHistoryDoc,
  office: AccountingOfficeDetail,
  options?: {
    test?: boolean;
    testRecipients?: string[];
    /** Office billing history; fetched when omitted. */
    history?: BillingHistoryDoc[];
  },
): Promise<SendAccountingResult> {
  const isTest = options?.test === true;
  const recipients = await resolveBillingSendRecipients(office);
  const intended = recipientsToIntended(recipients);
  const delivery = resolveActualRecipient(
    recipients,
    isTest,
    options?.testRecipients,
  );

  const blob = await generateAccountingPaymentReceiptPDFBlob(
    buildPaymentReceiptInputFromDoc(payment, office),
  );
  const pdfBase64 = await blobToBase64(blob);

  const officeName = officeLegalName(office);
  const reference = payment.reference ?? payment.id;
  const filename = `Payment_Receipt_${sanitizeFilenamePart(
    officeName,
  )}_${sanitizeFilenamePart(reference, 24)}.pdf`;
  const history =
    options?.history ?? (await getOfficeBillingHistory(office.id));
  const summary = summarizePaymentConfirmation(payment, history);
  const subject = summary.isPartial
    ? "WSG Partial Payment Confirmation"
    : "WSG Payment Confirmation";
  await sendBillingDocumentEmail({
    to: delivery.to,
    cc: delivery.cc,
    kind: summary.isPartial ? "payment-partial" : "payment",
    subject,
    templateVars: {
      fullname: recipients.primaryName || intended.name || officeName,
      amount: formatPaymentEmailAmount(summary.paid),
      invoiceAmount: formatPaymentEmailAmount(summary.invoiced),
    },
    filename,
    pdfBase64,
    ...(isTest ? { isTest: true } : {}),
  });

  return {
    sentTo: formatSentToLabel(delivery.to, delivery.cc),
    intended,
  };
}
